import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import './i18n/i18n';
import MetaTags from './components/common/MetaTags';
import Counter from './components/features/Counter';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import Settings from './components/pages/Settings';
import About from './components/pages/About';
import Landing from './components/pages/Landing';
import PrivacyPolicy from './components/pages/PrivacyPolicy';
import TermsOfService from './components/pages/TermsOfService';
import CounterDeleted from './components/pages/CounterDeleted';
import CounterNotFound from './components/pages/CounterNotFound';
import Performance from './components/pages/Performance';
import StripeWithdrawal from './components/pages/StripeWithdrawal';
import WithdrawalSetup from './components/pages/WithdrawalSetup';
import Wallet from './components/pages/Wallet';
import PurchaseHistory from './components/pages/PurchaseHistory';
import PurchaseResult from './components/pages/PurchaseResult';
import TokenTransactionHistory from './components/pages/TokenTransactionHistory';
import History from './components/features/History';
import Statistics from './components/features/Statistics';
import ButtonManager from './components/features/ButtonManager';
import CounterSettings from './components/pages/CounterSettings';
import CountersList from './components/features/CountersList';
import LocalStorageNotice from './components/LocalStorageNotice';
import { ThemeProvider } from './contexts/ThemeContext';
import { UserProvider } from './contexts/UserContext';
import { CounterProvider } from './contexts/CounterContext';
import { SocketProvider } from './contexts/SocketContext';
import UserProtectedRoute from './components/common/UserProtectedRoute';
import AdminProtectedRoute from './components/common/AdminProtectedRoute';
import { addSiteStructuredData } from './utils/metaTagsUtils';
import Signup from './components/pages/Signup';
import Login from './components/pages/Login';
import ForgotPassword from './components/pages/ForgotPassword';
import ResetPassword from './components/pages/ResetPassword';
import Account from './components/pages/Account';
import VerifyEmailChange from './components/pages/VerifyEmailChange';
import LoggedOut from './components/pages/LoggedOut';
import AdminSettings from './components/pages/AdminSettings';
import { config } from './config';
import { usePaymentUtils } from './utils/paymentUtils';

/**
 * ScrollToTop component
 * 
 * This component scrolls the page to the top whenever the route changes.
 */
const ScrollToTop: React.FC = () => {
  const location = useLocation();
  
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [location.pathname]);
  
  return null;
};

/**
 * PaymentRedirectHandler component
 * 
 * This component handles payment redirect callbacks from Stripe
 * by detecting session IDs in the URL.
 */
const PaymentRedirectHandler: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasSessionId } = usePaymentUtils();
  
  useEffect(() => {
    // Check for a session ID in the URL using the consolidated function
    const sessionId = hasSessionId();
    
    if (sessionId) {
      console.log("Payment redirect detected with session ID:", sessionId);
      
      // Don't clean the session ID immediately - let PurchaseResult handle it
      // Only navigate if we're not already on the purchase-result page
      if (!window.location.pathname.includes('purchase-result')) {
        navigate('/purchase-result');
      }
    }
  }, [location, navigate, hasSessionId]);
  
  return null;
};

/**
 * Main App component
 * Sets up routing and providers
 */
function App() {
  // Initialize site-wide structured data when the app loads
  useEffect(() => {
    addSiteStructuredData();
  }, []);

  return (
    <div className="app">
      <MetaTags />
      <UserProvider>
        <ThemeProvider>
          <CounterProvider>
            <SocketProvider>
              <Router>
                <ScrollToTop />
                <LocalStorageNotice />
                <PaymentRedirectHandler />
                <div className="d-flex flex-column min-vh-100 overflow-hidden position-relative w-100">
                  <Navbar />
                  <main className="flex-grow-1">
                    <Routes>
                      {/* Public routes - accessible to everyone */}
                      <Route path="/" element={<Landing />} />
                      <Route path="/about" element={<About />} />
                      <Route path="/terms" element={<TermsOfService />} />
                      <Route path="/privacy" element={<PrivacyPolicy />} />
                      <Route path="/counter-deleted" element={<CounterDeleted />} />
                      <Route path="/counter-not-found" element={<CounterNotFound />} />
                      
                      {/* Authentication routes */}
                      <Route path="/signup" element={<Signup />} />
                      <Route path="/login" element={<Login />} />
                      <Route path="/forgot-password" element={<ForgotPassword />} />
                      <Route path="/reset-password" element={<ResetPassword />} />
                      <Route path="/verify-email-change" element={<VerifyEmailChange />} />
                      <Route path="/logged-out" element={<LoggedOut />} />
                      
                      {/* Settings route - authenticated users only */}
                      <Route 
                        path="/settings" 
                        element={
                          <UserProtectedRoute>
                            <Settings />
                          </UserProtectedRoute>
                        } 
                      />
                      
                      {/* Account settings - authenticated users only */}
                      <Route 
                        path="/account" 
                        element={
                          <UserProtectedRoute>
                            <Account />
                          </UserProtectedRoute>
                        } 
                      />
                      
                      {/* Admin settings - authenticated users only (not in navigation) */}
                      <Route 
                        path={`/${config.auth.adminPath}`}
                        element={
                          <UserProtectedRoute>
                            <AdminSettings />
                          </UserProtectedRoute>
                        } 
                      />
                      
                      {/* Wallet route - authenticated users only */}
                      <Route 
                        path="/wallet" 
                        element={
                          <UserProtectedRoute>
                            <Wallet />
                          </UserProtectedRoute>
                        } 
                      />
                      
                      {/* Purchase History route - authenticated users only */}
                      <Route 
                        path="/purchase-history" 
                        element={
                          <UserProtectedRoute>
                            <PurchaseHistory />
                          </UserProtectedRoute>
                        } 
                      />
                      
                      {/* Purchase Result route - authenticated users only */}
                      <Route 
                        path="/purchase-result" 
                        element={
                          <UserProtectedRoute>
                            <PurchaseResult />
                          </UserProtectedRoute>
                        } 
                      />
                      
                      {/* Token Transactions route - authenticated users only */}
                      <Route 
                        path="/token-transactions" 
                        element={
                          <UserProtectedRoute>
                            <TokenTransactionHistory />
                          </UserProtectedRoute>
                        } 
                      />
                      
                      {/* Withdrawals route - authenticated users only */}
                      <Route 
                        path="/stripewithdrawal" 
                        element={
                          <UserProtectedRoute>
                            <StripeWithdrawal />
                          </UserProtectedRoute>
                        } 
                      />
                      
                      {/* Withdrawal Setup route - authenticated users only */}
                      <Route 
                        path="/withdrawal-setup" 
                        element={
                          <UserProtectedRoute>
                            <WithdrawalSetup />
                          </UserProtectedRoute>
                        } 
                      />
                      
                      {/* Performance monitoring - admin only */}
                      <Route 
                        path="/performance" 
                        element={
                          <AdminProtectedRoute>
                            <Performance />
                          </AdminProtectedRoute>
                        } 
                      />
                      
                      {/* Counter routes - the components handle parameters internally */}
                      <Route path="/counters" element={<CountersList />} />
                      <Route path="/:counterId" element={<Counter />} />
                      <Route path="/:counterId/counter-settings" element={<CounterSettings />} />
                      <Route path="/:counterId/history" element={<History />} />
                      <Route path="/:counterId/statistics" element={<Statistics />} />
                      <Route path="/:counterId/buttons" element={<ButtonManager />} />
                      
                      {/* Fallback route */}
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                  </main>
                  <Footer />
                </div>
              </Router>
            </SocketProvider>
          </CounterProvider>
        </ThemeProvider>
      </UserProvider>
    </div>
  );
}

export default App; 
